import { useState, useEffect } from 'react';
import { doc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { readOfflineContentCache, writeOfflineContentCache } from '../../../utils/offlineContentCache';
import { useOnlineRefreshSignal } from '../../../utils/onlineStatus';

const CACHE_SCOPE = 'messages';

/**
 * useThread hook
 *
 * Subscribes to a single thread document and returns its data.
 *
 * @param {string} threadId - ID of the thread
 * @returns {{ thread: object|null, loading: boolean }}
 */
export default function useThread(threadId, routeContext = {}, currentUserId = '', enabled = true) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const onlineRefreshSignal = useOnlineRefreshSignal();
  useEffect(() => {
    if (!threadId || !enabled) {
      setThread(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const cacheParts = [currentUserId || 'unknown', CACHE_SCOPE, 'thread', threadId];
    setThread(readOfflineContentCache(cacheParts, null));
    const threadRef = doc(db, 'messages', threadId);
    const unsub = safeOnSnapshot(threadRef, snap => {
      if (snap.exists()) {
        const nextThread = {
          id: snap.id,
          ...snap.data()
        };
        setThread(nextThread);
        writeOfflineContentCache(cacheParts, nextThread);
      } else {
        setThread(null);
      }
      setLoading(false);
    }, err => {
      setLoading(false);
    }, { label: 'messages.thread', threadId, ...routeContext });
    return () => unsub();
  }, [currentUserId, enabled, onlineRefreshSignal, routeContext, threadId]);
  return {
    thread,
    loading
  };
}
